import i18next from "i18next";

import { IRagRule, RagSex } from "../../@types/Rag";

export type RuleError = {
  measurementType?: string;
  sex?: string;
  color?: string;
  startValue?: string;
  endValue?: string;
  label?: string;
  overlap?: string;
};

const COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

const SEX_VALUES = new Set(["Male", "Female", "Both"]);

function validateRule(
  rule: IRagRule,
  availableMeasurements?: Set<string>
): RuleError {
  const error: RuleError = {};

  // EN-5123: the valid measurements are whatever the config's wards collect, so they are passed in
  // rather than hardcoded. Without a set (wards not loaded yet) only presence is checked.
  if (!rule.measurementType) {
    error.measurementType = i18next.t("rag_validation_required");
  } else if (
    availableMeasurements &&
    !availableMeasurements.has(rule.measurementType)
  ) {
    error.measurementType = i18next.t("rag_validation_notAvailableOnWards");
  }
  if (!SEX_VALUES.has(rule.sex)) {
    error.sex = i18next.t("rag_validation_required");
  }
  if (!rule.color || !COLOR_REGEX.test(rule.color)) {
    error.color = i18next.t("rag_validation_invalidColor");
  }
  if (typeof rule.startValue !== "number" || Number.isNaN(rule.startValue)) {
    error.startValue = i18next.t("rag_validation_required");
  }
  if (typeof rule.endValue !== "number" || Number.isNaN(rule.endValue)) {
    error.endValue = i18next.t("rag_validation_required");
  }
  if (
    !error.startValue &&
    !error.endValue &&
    rule.endValue <= rule.startValue
  ) {
    error.endValue = i18next.t("rag_validation_endGreaterThanStart");
  }
  if (rule.label && rule.label.length > 50) {
    error.label = i18next.t("rag_validation_labelMaxLength");
  }

  return error;
}

function sexesOverlap(a: RagSex, b: RagSex): boolean {
  if (a === "Both" || b === "Both") return true;
  return a === b;
}

const startIsInclusive = (rule: IRagRule) =>
  rule.startComparator === "GreaterThanOrEqualTo";
const endIsInclusive = (rule: IRagRule) =>
  rule.endComparator === "LessThanOrEqualTo";

// Two rules' value ranges overlap when neither sits entirely to one side of the
// other. Boundaries that meet at the same value only collide when both are
// inclusive (e.g. ≤100 and ≥100 → clash; ≤100 and >100 → adjacent, fine),
// mirroring the backend's RagConfigRuleOverlap check.
function rangesOverlap(a: IRagRule, b: IRagRule): boolean {
  if (
    Number.isNaN(a.startValue) ||
    Number.isNaN(a.endValue) ||
    Number.isNaN(b.startValue) ||
    Number.isNaN(b.endValue)
  ) {
    return false;
  }

  const aLeftOfB =
    a.endValue < b.startValue ||
    (a.endValue === b.startValue &&
      !(endIsInclusive(a) && startIsInclusive(b)));
  const bLeftOfA =
    b.endValue < a.startValue ||
    (b.endValue === a.startValue &&
      !(endIsInclusive(b) && startIsInclusive(a)));

  return !(aLeftOfB || bLeftOfA);
}

function formatRange(rule: IRagRule): string {
  const start = startIsInclusive(rule) ? "≥" : ">";
  const end = endIsInclusive(rule) ? "≤" : "<";
  return `${start}${rule.startValue} & ${end}${rule.endValue}`;
}

function describeRangeRelation(
  thisRule: IRagRule,
  otherRule: IRagRule
): string {
  return i18next.t("rag_validation_rangeOverlaps", {
    thisRange: formatRange(thisRule),
    otherRange: formatRange(otherRule),
  });
}

/**
 * The sex written out, for use inside the overlap sentence. Deliberately NOT the
 * `rag_rule_sex*` keys — those are the dropdown's one-letter abbreviations ("M"/"F"), which
 * read wrong mid-sentence. `RagSex` itself stays the API value (EN-5191).
 */
function sexLabel(sex: RagSex): string {
  if (sex === "Male") return i18next.t("rag_validation_sexMale");
  if (sex === "Female") return i18next.t("rag_validation_sexFemale");
  return i18next.t("rag_validation_sexBoth");
}

function describeSexRelation(
  thisSex: RagSex,
  otherSex: RagSex,
  otherIndex: number
): string {
  if (thisSex === otherSex) {
    return thisSex === "Both"
      ? i18next.t("rag_validation_bothCoverAllSexes")
      : i18next.t("rag_validation_bothCoverSex", { sex: sexLabel(thisSex) });
  }
  if (thisSex === "Both") {
    return i18next.t("rag_validation_thisCoversAllSexes", {
      index: otherIndex,
      otherSex: sexLabel(otherSex),
    });
  }
  return i18next.t("rag_validation_otherCoversAllSexes", {
    index: otherIndex,
    sex: sexLabel(thisSex),
  });
}

export function validateRules(
  rules: IRagRule[],
  availableMeasurements?: Set<string>
): RuleError[] {
  const errors: RuleError[] = rules.map((rule) =>
    validateRule(rule, availableMeasurements)
  );

  for (let i = 0; i < rules.length; i += 1) {
    for (let j = i + 1; j < rules.length; j += 1) {
      const a = rules[i];
      const b = rules[j];
      const conflict =
        a.measurementType === b.measurementType &&
        rangesOverlap(a, b) &&
        sexesOverlap(a.sex, b.sex);
      if (conflict) {
        const rangeDesc = describeRangeRelation(b, a);
        const sexDesc = describeSexRelation(b.sex, a.sex, i + 1);
        errors[j] = {
          ...errors[j],
          overlap: i18next.t("rag_validation_conflictsWithRule", {
            index: i + 1,
            rangeDescription: rangeDesc,
            sexDescription: sexDesc,
          }),
        };
      }
    }
  }

  return errors;
}

export function hasAnyError(errors: RuleError[]): boolean {
  return errors.some((e) => Object.keys(e).length > 0);
}
